import React, { useState } from "react";
import { useTranslation } from "react-i18next";

export default function CancelSubscriptionModal({
  open,
  onClose,
  onConfirm,
  periodEnd,
  immediate,
  loading,
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");

  const REASONS = [
    { value: "too_expensive", label: t("billing.cancelReasonExpensive") },
    { value: "no_longer_needed", label: t("billing.cancelReasonNoLongerNeeded") },
    { value: "switching_platform", label: t("billing.cancelReasonSwitching") },
    { value: "missing_features", label: t("billing.cancelReasonMissingFeatures") },
    { value: "other", label: t("billing.cancelReasonOther") },
  ];

  if (!open) return null;

  const handleConfirm = () => {
    onConfirm({ reason: reason || "other", comment: comment.trim() });
  };

  const periodEndFormatted = periodEnd
    ? new Date(periodEnd).toLocaleDateString("ro-RO", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0D1117] p-6 shadow-2xl">
        <h2 className="text-xl font-semibold text-white">{t("billing.cancelTitle")}</h2>

        {immediate ? (
          <p className="mt-3 text-sm text-white/70">
            {t("billing.cancelImmediate")}
          </p>
        ) : (
          <p className="mt-3 text-sm text-white/70">
            {t("billing.cancelAtEnd", { date: periodEndFormatted })}
          </p>
        )}

        <div className="mt-5">
          <div className="text-sm text-white/60 mb-2">
            {t("billing.cancelReasonLabel")}
          </div>
          <div className="space-y-2">
            {REASONS.map((r) => (
              <label
                key={r.value}
                className={[
                  "flex items-center gap-3 rounded-xl border px-3 py-2 cursor-pointer",
                  reason === r.value
                    ? "border-white/30 bg-white/10"
                    : "border-white/10 hover:bg-white/5",
                ].join(" ")}
              >
                <input
                  type="radio"
                  name="cancel-reason"
                  value={r.value}
                  checked={reason === r.value}
                  onChange={() => setReason(r.value)}
                  className="accent-red-400"
                />
                <span className="text-sm text-white">{r.label}</span>
              </label>
            ))}
          </div>
        </div>

        {reason === "other" || reason === "missing_features" ? (
          <div className="mt-4">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("billing.cancelComment")}
              maxLength={1000}
              rows={3}
              className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-white/20 focus:outline-none"
            />
          </div>
        ) : null}

        <div className="mt-6 flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-3 rounded-xl border border-white/15 text-white hover:bg-white/10 disabled:opacity-50"
          >
            {t("billing.keepBtn")}
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 px-4 py-3 rounded-xl bg-red-500/90 text-white font-medium hover:bg-red-500 disabled:opacity-50"
          >
            {loading ? t("billing.cancellingBtn") : t("billing.cancelTitle")}
          </button>
        </div>
      </div>
    </div>
  );
}
